import React from 'react';
import { ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import type { Page, Component } from '../ComponentEditModal.types';
import { PageSelector } from '../shared/PageSelector';
import { ImageEditorWithCrop } from '../shared/ImageThumbnailWithCrop';
import type { CropData } from '../shared/ImageCropModal';
import type { Area } from 'react-easy-crop';

export interface ImageInfoItem {
  id: string;
  imageUrl: string;
  cloudfrontUrl?: string;
  alt?: string;
  subtitle?: string;
  description?: string;
  order: number;
  crop?: CropData;
  button?: {
    text: string;
    url: string;
    style: string;
    target: string;
  };
}

interface ImageInfoItemCardProps {
  item: ImageInfoItem;
  index: number;
  totalItems: number;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
  onUpdate: (field: string, value: any) => void;
  onUpdateFields?: (fields: Record<string, any>) => void;
  onRemove: () => void;
  onMove: (direction: 'up' | 'down') => void;
  onUpload: () => void;
}

export const ImageInfoItemCard: React.FC<ImageInfoItemCardProps> = ({
  item,
  index,
  totalItems,
  pages,
  isLoadingPages,
  currentPageComponents,
  onUpdate,
  onUpdateFields,
  onRemove,
  onMove,
  onUpload,
}) => {
  return (
    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-700">Item {index + 1}</span>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => onMove('up')}
            disabled={index === 0}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
            title="Move up"
          >
            <ChevronUp size={16} />
          </button>
          <button
            type="button"
            onClick={() => onMove('down')}
            disabled={index === totalItems - 1}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
            title="Move down"
          >
            <ChevronDown size={16} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="p-1 text-red-400 hover:text-red-600"
            title="Remove item"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {/* Image with Crop */}
        <ImageEditorWithCrop
          imageUrl={item.imageUrl}
          cloudfrontUrl={item.cloudfrontUrl}
          crop={item.crop}
          onUrlChange={url => onUpdate('imageUrl', url)}
          onUpload={onUpload}
          onRemove={() => {
            if (onUpdateFields) {
              onUpdateFields({ imageUrl: '', cloudfrontUrl: '', crop: undefined });
            } else {
              onUpdate('imageUrl', '');
              onUpdate('cloudfrontUrl', '');
              onUpdate('crop', undefined);
            }
          }}
          onCropChange={(crop: CropData, _croppedAreaPixels: Area) => {
            onUpdate('crop', crop);
          }}
          label="Image"
          required
          cropMode="frame"
          lockedAspect={1}
        />

        {/* Alt Text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Alt Text</label>
          <input
            type="text"
            value={item.alt || ''}
            onChange={e => onUpdate('alt', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Descriptive alt text for accessibility"
            maxLength={200}
          />
        </div>

        {/* Subtitle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
          <input
            type="text"
            value={item.subtitle || ''}
            onChange={e => onUpdate('subtitle', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="e.g., Our Vision"
            maxLength={200}
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <textarea
            value={item.description || ''}
            onChange={e => onUpdate('description', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Description text..."
            rows={3}
            maxLength={1000}
          />
        </div>

        {/* Button Section */}
        <ButtonSection
          item={item}
          pages={pages}
          isLoadingPages={isLoadingPages}
          currentPageComponents={currentPageComponents}
          onUpdate={onUpdate}
        />
      </div>
    </div>
  );
};

// Sub-component for button

interface ButtonSectionProps {
  item: ImageInfoItem;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
  onUpdate: (field: string, value: any) => void;
}

const ButtonSection: React.FC<ButtonSectionProps> = ({
  item,
  pages,
  isLoadingPages,
  currentPageComponents,
  onUpdate,
}) => {
  const handleToggleButton = (checked: boolean) => {
    if (checked) {
      onUpdate('button', {
        text: 'Learn More',
        url: '/',
        style: 'primary',
        target: '_self',
      });
    } else {
      onUpdate('button', undefined);
    }
  };

  return (
    <div className="border-t pt-3 mt-3">
      <label className="flex items-center space-x-2 mb-2">
        <input
          type="checkbox"
          checked={!!item.button}
          onChange={e => handleToggleButton(e.target.checked)}
          className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
        />
        <span className="text-sm font-medium text-gray-700">Show Button</span>
      </label>

      {item.button && (
        <div className="space-y-3 pl-6 border-l-2 border-gray-200">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Button Text</label>
            <input
              type="text"
              value={item.button?.text || ''}
              onChange={e => onUpdate('button', { ...item.button, text: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Learn More"
              maxLength={30}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Button URL</label>
            <PageSelector
              value={item.button?.url || ''}
              onChange={url => onUpdate('button', { ...item.button, url })}
              pages={pages}
              isLoadingPages={isLoadingPages}
              currentPageComponents={currentPageComponents}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Button Style</label>
              <select
                value={item.button?.style || 'primary'}
                onChange={e => onUpdate('button', { ...item.button, style: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
                <option value="outline">Outline</option>
                <option value="ghost">Ghost</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={item.button?.target === '_blank'}
                  onChange={e =>
                    onUpdate('button', {
                      ...item.button,
                      target: e.target.checked ? '_blank' : '_self',
                    })
                  }
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-xs text-gray-700">Open in new tab</span>
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
